import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTasks, updateTask, deleteTask, getUsers, getClients, getLeads, uploadTaskAttachment, getReportUrl } from '../../lib/api';
import { formatDate, getPriorityBadgeClass, getStatusBadgeClass, formatApiError, isOverdue, formatCurrency } from '../../lib/utils';
import { PRIORITIES, TASK_STATUSES, REJECTION_REASONS, MANDATE_STATUSES, MANDATE_TASK_TYPES, AMC_NAMES, TASK_CATEGORY_MAP, buildUserOptions } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { DatePicker } from '../../components/ui/date-picker';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, DotsThree, Check, Clock, Trash, ClipboardText, Warning, ArrowsClockwise, Eye, Paperclip, DownloadSimple, CalendarBlank } from '@phosphor-icons/react';

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState({});
  const [clients, setClients] = useState({});
  const [leads, setLeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [amFilter, setAmFilter] = useState('all');
  const [usersList, setUsersList] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const navigate = useNavigate();

  // Status update dialog
  const [updatingTask, setUpdatingTask] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: '', status_comment: '', rejection_reason: '',
    mandate_status: '', mandate_amc_name: '', due_date: ''
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (typeFilter !== 'all') params.task_type = typeFilter;
      if (amFilter !== 'all') params.assigned_to = amFilter;
      if (dateFrom) params.due_date_from = dateFrom;
      if (dateTo) params.due_date_to = dateTo;

      const [tasksRes, usersRes, clientsRes, leadsRes] = await Promise.all([
        getTasks(params), getUsers(), getClients(), getLeads()
      ]);
      setTasks(tasksRes.data);
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
      setUsers(usersMap);
      setUsersList(usersRes.data);
      const clientsMap = {};
      clientsRes.data.forEach(c => { clientsMap[c.id] = c.full_name; });
      setClients(clientsMap);
      const leadsMap = {};
      (leadsRes.data || []).forEach(l => { leadsMap[l.id] = l.full_name || l.company_name || l.name || 'Lead'; });
      setLeads(leadsMap);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter, priorityFilter, typeFilter, dateFrom, dateTo]);

  const handleStatusUpdate = async () => {
    if (!updatingTask) return;
    try {
      const payload = { ...statusForm };
      if (payload.status !== 'Rejection') {
        payload.rejection_reason = null;
      }
      if (!MANDATE_TASK_TYPES.includes(updatingTask.task_type)) {
        payload.mandate_status = null;
        payload.mandate_amc_name = null;
      }
      const res = await updateTask(updatingTask.id, payload);
      setTasks(tasks.map(t => t.id === updatingTask.id ? res.data : t));
      setUpdatingTask(null);
      setStatusForm({ status: '', status_comment: '', rejection_reason: '', mandate_status: '', mandate_amc_name: '', due_date: '' });
      toast.success(`Task status updated to ${statusForm.status}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      const res = await updateTask(taskId, { status: newStatus, status_comment: '' });
      setTasks(tasks.map(t => t.id === taskId ? res.data : t));
      toast.success(`Task status updated to ${newStatus}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteTask(deleteId);
      setTasks(tasks.filter(t => t.id !== deleteId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const handleFileUpload = async (taskId, file) => {
    setUploadingFile(true);
    try {
      const res = await uploadTaskAttachment(taskId, file);
      setTasks(tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, attachments: [...(t.attachments || []), res.data] };
        }
        return t;
      }));
      if (updatingTask?.id === taskId) {
        setUpdatingTask(prev => ({
          ...prev,
          attachments: [...(prev.attachments || []), res.data]
        }));
      }
      toast.success('Attachment uploaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingFile(false);
    }
  };

  const [quickFilter, setQuickFilter] = useState('all');
  const overdueCount = tasks.filter(t => !['Completed', 'Rejection'].includes(t.status) && isOverdue(t.due_date)).length;
  const _today = new Date(); _today.setHours(0,0,0,0);
  const _tomorrow = new Date(_today); _tomorrow.setDate(_today.getDate() + 1);
  const todayStr = _today.toISOString().split('T')[0];
  const tomorrowStr = _tomorrow.toISOString().split('T')[0];
  const dueInDays = (d) => {
    if (!d) return Infinity;
    const dt = new Date(d); dt.setHours(0,0,0,0);
    return Math.floor((dt - _today) / 86400000);
  };
  const isOpen = (t) => !['Completed', 'Rejection'].includes(t.status);
  const todayCount = tasks.filter(t => isOpen(t) && t.due_date === todayStr).length;
  const tomorrowCount = tasks.filter(t => isOpen(t) && t.due_date === tomorrowStr).length;
  const next3Count = tasks.filter(t => isOpen(t) && dueInDays(t.due_date) >= 0 && dueInDays(t.due_date) <= 3).length;
  const visibleTasks = tasks.filter(t => {
    if (quickFilter === 'all') return true;
    if (quickFilter === 'overdue') return isOpen(t) && isOverdue(t.due_date);
    if (quickFilter === 'today') return isOpen(t) && t.due_date === todayStr;
    if (quickFilter === 'tomorrow') return isOpen(t) && t.due_date === tomorrowStr;
    if (quickFilter === 'next3') return isOpen(t) && dueInDays(t.due_date) >= 0 && dueInDays(t.due_date) <= 3;
    return true;
  });

  const openStatusDialog = (task) => {
    setUpdatingTask(task);
    setStatusForm({
      status: task.status, status_comment: '', rejection_reason: '',
      mandate_status: task.mandate_status || '', mandate_amc_name: task.mandate_amc_name || '',
      due_date: task.due_date || ''
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Tasks</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage operational tasks</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="export-tasks-btn">
                <DownloadSimple size={18} className="mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => window.open(getReportUrl('tasks', 'csv'), '_blank')}>
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(getReportUrl('tasks', 'excel'), '_blank')}>
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => navigate('/tasks/new')} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="create-task-button">
            <Plus size={18} className="mr-2" /> New Task
          </Button>
        </div>
      </div>

      {/* Quick Filter Cards — click to filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: 'all', label: 'All Open', count: tasks.filter(isOpen).length, color: 'zinc', icon: ClipboardText },
          { key: 'overdue', label: 'Overdue', count: overdueCount, color: 'red', icon: Warning },
          { key: 'today', label: 'Due Today', count: todayCount, color: 'amber', icon: Clock },
          { key: 'tomorrow', label: 'Tomorrow', count: tomorrowCount, color: 'blue', icon: CalendarBlank },
          { key: 'next3', label: 'Next 3 Days', count: next3Count, color: 'emerald', icon: CalendarBlank },
        ].map(chip => {
          const Icon = chip.icon;
          const active = quickFilter === chip.key;
          const ringMap = { red: 'ring-red-500', amber: 'ring-amber-500', blue: 'ring-blue-500', emerald: 'ring-emerald-500', zinc: 'ring-zinc-900' };
          const bgMap = { red: 'bg-red-50 text-red-600', amber: 'bg-amber-50 text-amber-600', blue: 'bg-blue-50 text-blue-600', emerald: 'bg-emerald-50 text-emerald-600', zinc: 'bg-zinc-100 text-zinc-600' };
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setQuickFilter(chip.key)}
              className={`rounded-sm border bg-white p-3 text-left transition-all hover:border-zinc-400 ${active ? `border-transparent ring-2 ${ringMap[chip.color]}` : 'border-zinc-200'}`}
              data-testid={`task-quick-filter-${chip.key}`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-sm ${bgMap[chip.color]}`}><Icon size={14} weight="fill" /></div>
                <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-zinc-500">{chip.label}</span>
              </div>
              <p className={`text-2xl font-bold tabular-nums mt-1 ${chip.color === 'red' && chip.count > 0 ? 'text-red-600' : ''}`}>{chip.count}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 overflow-x-auto pb-1 filter-bar">
        <SearchableSelect
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[{ value: 'active', label: 'Active (excl. Completed)' }, { value: 'all', label: 'All Statuses' }, ...TASK_STATUSES.map(s => ({ value: s, label: s }))]}
          placeholder="Status"
          triggerClassName="w-[200px]"
          data-testid="task-status-filter"
        />
        <SearchableSelect
          value={priorityFilter}
          onValueChange={setPriorityFilter}
          options={[{ value: 'all', label: 'All Priorities' }, ...PRIORITIES.map(p => ({ value: p, label: p }))]}
          placeholder="Priority"
          triggerClassName="w-[140px]"
        />
        <SearchableSelect
          value={typeFilter}
          onValueChange={setTypeFilter}
          groups={[{ label: '', options: [{ value: 'all', label: 'All Types' }] }, ...Object.entries(TASK_CATEGORY_MAP).map(([cat, types]) => ({ label: cat, options: types }))]}
          placeholder="Task Type"
          triggerClassName="w-[180px]"
          data-testid="task-type-filter"
        />
        <SearchableSelect
          value={amFilter}
          onValueChange={setAmFilter}
          options={[{ value: 'all', label: 'All Members' }, ...buildUserOptions(usersList.filter(u => u.status === 'active' || u.id))]}
          placeholder="Team (AM / SAM / OM)"
          searchPlaceholder="Search..."
          triggerClassName="w-[180px]"
          data-testid="task-am-filter"
        />
        <div className="flex items-center gap-2">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" className="w-[150px]" data-testid="task-date-from" />
          <span className="text-zinc-400 text-sm">to</span>
          <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" className="w-[150px]" data-testid="task-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs">Clear dates</Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        {loading ? (
          <div className="p-6 space-y-4">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}</div>
        ) : visibleTasks.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardText size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No tasks found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Task Type</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Priority</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Due Date</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Assigned To</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Details</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleTasks.map((task) => {
                const isOverdueItem = !['Completed', 'Rejection'].includes(task.status) && isOverdue(task.due_date);
                const isMandateTask = MANDATE_TASK_TYPES.includes(task.task_type);
                return (
                  <TableRow key={task.id} className={`hover:bg-zinc-50 ${isOverdueItem ? 'bg-red-50/50' : ''}`} data-testid={`task-row-${task.id}`}>
                    <TableCell>
                      <button
                        className="font-medium text-sm text-[#002FA7] hover:underline cursor-pointer bg-transparent border-0 p-0 text-left"
                        onClick={() => setViewingTask(task)}
                        data-testid={`view-task-${task.id}`}
                      >
                        {task.task_type}
                      </button>
                      {task.attachments?.length > 0 && (
                        <span className="text-zinc-400 text-xs inline-flex items-center gap-1 mt-0.5">
                          <Paperclip size={12} /> {task.attachments.length}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {task.client_id && (clients[task.client_id] || task.client_name) ? (
                        <Link to={`/clients/${task.client_id}`} className="text-sm hover:text-[#002FA7]">
                          {clients[task.client_id] || task.client_name}
                        </Link>
                      ) : task.lead_id && (leads[task.lead_id] || task.lead_name) ? (
                        <Link to={`/leads/${task.lead_id}`} className="text-sm hover:text-[#002FA7]">
                          {leads[task.lead_id] || task.lead_name} <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-1">Lead</span>
                        </Link>
                      ) : (
                        <span className="text-sm text-zinc-400">Contact unavailable</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {task.amount_type === 'all_units' ? (
                        <span className="text-sm text-zinc-700 italic">All Units</span>
                      ) : task.amount_type === 'units' && task.units != null ? (
                        <span className="text-sm font-medium tabular-nums">{Number(task.units).toLocaleString()} units</span>
                      ) : task.amount ? (
                        <span className="text-sm font-medium tabular-nums">{formatCurrency(task.amount)}</span>
                      ) : (
                        <span className="text-xs text-zinc-300">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="cursor-pointer" data-testid={`task-status-badge-${task.id}`}>
                              <Badge className={`${getStatusBadgeClass(task.status)} text-xs hover:opacity-80 transition-opacity`}>{task.status}</Badge>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-40">
                            {TASK_STATUSES.filter(s => s !== task.status).map(status => (
                              <DropdownMenuItem
                                key={status}
                                onClick={() => status === 'Rejection' ? openStatusDialog(task) : handleQuickStatusChange(task.id, status)}
                                data-testid={`quick-status-${task.id}-${status}`}
                              >
                                <Badge className={`${getStatusBadgeClass(status)} text-xs mr-2`}>{status}</Badge>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {task.rejection_reason && <Badge className="bg-red-100 text-red-700 text-[10px]">{task.rejection_reason}</Badge>}
                        {isOverdueItem && <Badge className="badge-overdue text-xs">Overdue</Badge>}
                        {isMandateTask && task.mandate_status && (
                          <Badge className="bg-violet-100 text-violet-700 text-[10px]">M: {task.mandate_status}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getPriorityBadgeClass(task.priority)} text-xs`}>{task.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{formatDate(task.due_date)}</p>
                      {task.due_time && <p className="text-xs text-zinc-400">{task.due_time}</p>}
                    </TableCell>
                    <TableCell className="text-sm">{users[task.assigned_to] || 'Unassigned'}</TableCell>
                    <TableCell>
                      <div className="text-xs text-zinc-500 space-y-0.5">
                        {task.folio_number && <p>Folio: {task.folio_number}</p>}
                        {task.amc_name && <p>AMC: {task.amc_name}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => openStatusDialog(task)} className="rounded-sm text-xs h-7 px-2" data-testid={`update-task-${task.id}`}>
                          <ArrowsClockwise size={14} className="mr-1" /> Update
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><DotsThree size={16} /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDeleteId(task.id)} className="text-red-600">
                              <Trash size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      {/* Status Update Dialog */}
      <Dialog open={!!updatingTask} onOpenChange={() => setUpdatingTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Task: {updatingTask?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Status *</Label>
              <SearchableSelect
                value={statusForm.status}
                onValueChange={(v) => setStatusForm(p => ({ ...p, status: v }))}
                options={TASK_STATUSES}
                placeholder="Select status"
                data-testid="task-update-status"
              />
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Due Date</Label>
              <DatePicker
                value={statusForm.due_date || ''}
                onChange={(v) => setStatusForm(p => ({ ...p, due_date: v }))}
                placeholder="Select due date"
                data-testid="task-update-due-date"
              />
            </div>

            {/* Rejection Reason */}
            {statusForm.status === 'Rejection' && (
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold text-red-600">Rejection Reason *</Label>
                <SearchableSelect
                  value={statusForm.rejection_reason}
                  onValueChange={(v) => setStatusForm(p => ({ ...p, rejection_reason: v }))}
                  options={REJECTION_REASONS}
                  placeholder="Select reason"
                  data-testid="task-rejection-reason"
                />
              </div>
            )}

            {/* Mandate Status (for SIP Start & SIP Modification only) */}
            {updatingTask && MANDATE_TASK_TYPES.includes(updatingTask.task_type) && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-semibold text-violet-600">Mandate Status</Label>
                  <SearchableSelect
                    value={statusForm.mandate_status}
                    onValueChange={(v) => setStatusForm(p => ({ ...p, mandate_status: v }))}
                    options={MANDATE_STATUSES}
                    placeholder="Select mandate status"
                    data-testid="task-mandate-status"
                  />
                </div>
                {(statusForm.mandate_status === 'Mandate Created with AMC') && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-semibold">AMC Name *</Label>
                    <SearchableSelect
                      value={statusForm.mandate_amc_name}
                      onValueChange={(v) => setStatusForm(p => ({ ...p, mandate_amc_name: v }))}
                      options={AMC_NAMES}
                      placeholder="Select AMC"
                      searchPlaceholder="Search AMC..."
                      data-testid="task-mandate-amc"
                    />
                  </div>
                )}
              </>
            )}

            {/* Comments (always shown) */}
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Comments</Label>
              <Textarea
                value={statusForm.status_comment}
                onChange={(e) => setStatusForm(p => ({ ...p, status_comment: e.target.value }))}
                placeholder="Add comments about this status update..."
                className="rounded-sm min-h-[80px]"
                data-testid="task-status-comment"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Attachments</Label>
              {updatingTask?.attachments?.length > 0 && (
                <div className="space-y-1 mb-2">
                  {updatingTask.attachments.map(a => (
                    <div key={a.id} className="flex items-center gap-2 text-xs p-2 bg-zinc-50 rounded">
                      <Paperclip size={14} className="text-zinc-500" />
                      <span className="flex-1 truncate">{a.file_name}</span>
                      <span className="text-zinc-400">{(a.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files[0] && updatingTask) {
                      handleFileUpload(updatingTask.id, e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingFile}
                  className="rounded-sm text-xs"
                  data-testid="task-attachment-input"
                />
                {uploadingFile && <span className="text-xs text-zinc-500">Uploading...</span>}
              </div>
            </div>

            {/* Status History */}
            {updatingTask?.status_history?.length > 0 && (
              <div className="border-t border-zinc-100 pt-4">
                <Label className="text-xs uppercase font-semibold text-zinc-500 mb-2 block">Previous Updates</Label>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {updatingTask.status_history.map((h, i) => (
                    <div key={h.changed_at || `hist-${i}`} className="text-xs p-2 bg-zinc-50 rounded">
                      <div className="flex items-center gap-2">
                        <Badge className={`${getStatusBadgeClass(h.from_status)} text-[10px]`}>{h.from_status}</Badge>
                        <span>→</span>
                        <Badge className={`${getStatusBadgeClass(h.to_status)} text-[10px]`}>{h.to_status}</Badge>
                        <span className="text-zinc-400 ml-auto">{h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}</span>
                      </div>
                      {h.comment && <p className="mt-1 text-zinc-600">{h.comment}</p>}
                      {h.rejection_reason && <p className="mt-1 text-red-600">Rejection: {h.rejection_reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setUpdatingTask(null)}>Cancel</Button>
              <Button
                onClick={handleStatusUpdate}
                disabled={statusForm.status === 'Rejection' && !statusForm.rejection_reason}
                className="bg-[#002FA7] text-white"
                data-testid="confirm-task-update"
              >
                Update Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>

      {/* Task Detail View Dialog */}
      <Dialog open={!!viewingTask} onOpenChange={() => setViewingTask(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={20} className="text-[#002FA7]" />
              Task Details
            </DialogTitle>
          </DialogHeader>
          {viewingTask && (
            <div className="space-y-4 py-2" data-testid="task-detail-popup">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Task Type</p>
                  <p className="font-medium">{viewingTask.task_type}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Status</p>
                  <Badge className={`${getStatusBadgeClass(viewingTask.status)} text-xs`}>{viewingTask.status}</Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Priority</p>
                  <Badge className={`${getPriorityBadgeClass(viewingTask.priority)} text-xs`}>{viewingTask.priority}</Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">
                    {viewingTask.lead_id ? 'Lead' : 'Client'}
                  </p>
                  {viewingTask.client_id ? (
                    <Link to={`/clients/${viewingTask.client_id}`} className="text-[#002FA7] hover:underline text-sm">
                      {clients[viewingTask.client_id] || viewingTask.client_name || 'Unknown'}
                    </Link>
                  ) : viewingTask.lead_id ? (
                    <Link to={`/leads/${viewingTask.lead_id}`} className="text-[#002FA7] hover:underline text-sm">
                      {leads[viewingTask.lead_id] || viewingTask.lead_name || 'Unknown'}
                    </Link>
                  ) : (
                    <span className="text-sm text-zinc-400">—</span>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Due Date</p>
                  <p className="text-sm">{formatDate(viewingTask.due_date)} {viewingTask.due_time || ''}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Assigned To</p>
                  <p className="text-sm">{users[viewingTask.assigned_to] || 'Unassigned'}</p>
                </div>
                {viewingTask.amc_name && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">AMC Name</p>
                    <p className="text-sm">{viewingTask.amc_name}</p>
                  </div>
                )}
                {viewingTask.folio_number && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Folio Number</p>
                    <p className="text-sm">{viewingTask.folio_number}</p>
                  </div>
                )}
                {viewingTask.folio_type && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Folio Type</p>
                    <p className="text-sm capitalize">{viewingTask.folio_type}</p>
                  </div>
                )}
                {viewingTask.pan_number && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">PAN Number</p>
                    <p className="text-sm font-mono uppercase">{viewingTask.pan_number}</p>
                  </div>
                )}
                {viewingTask.kyc_status && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">KYC Status</p>
                    <p className="text-sm">{viewingTask.kyc_status}</p>
                  </div>
                )}
                {viewingTask.amount_type === 'all_units' ? (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Value</p>
                    <p className="text-sm italic text-zinc-700">All Units</p>
                  </div>
                ) : viewingTask.amount_type === 'units' && viewingTask.units != null ? (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Units</p>
                    <p className="text-sm font-medium tabular-nums">{Number(viewingTask.units).toLocaleString()}</p>
                  </div>
                ) : viewingTask.amount ? (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Amount</p>
                    <p className="text-sm font-medium text-green-700">{formatCurrency(viewingTask.amount)}</p>
                  </div>
                ) : null}
                {viewingTask.purchase_sub_type && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Purchase Type</p>
                    <p className="text-sm">{viewingTask.purchase_sub_type}</p>
                  </div>
                )}
                {viewingTask.mandate_status && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Mandate Status</p>
                    <p className="text-sm">{viewingTask.mandate_status}</p>
                  </div>
                )}
                {viewingTask.rejection_reason && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Rejection Reason</p>
                    <p className="text-sm text-red-600">{viewingTask.rejection_reason}</p>
                  </div>
                )}
              </div>

              {viewingTask.description && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-1">Description</p>
                  <p className="text-sm text-zinc-700 bg-zinc-50 p-3 rounded-sm">{viewingTask.description}</p>
                </div>
              )}

              {/* Attachments */}
              {viewingTask.attachments?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Attachments</p>
                  <div className="space-y-2">
                    {viewingTask.attachments.map((att, idx) => (
                      <a
                        key={att.url || att.key || `att-${idx}`}
                        href={att.url || att}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-blue-50 rounded-sm border border-blue-100 hover:bg-blue-100 transition-colors"
                        data-testid={`task-attachment-link-${idx}`}
                      >
                        <Paperclip size={16} className="text-[#002FA7]" />
                        <span className="text-sm text-[#002FA7] flex-1 truncate">{att.name || att.filename || `Attachment ${idx + 1}`}</span>
                        <DownloadSimple size={16} className="text-zinc-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {viewingTask.attachment_url && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Attachment</p>
                  <a
                    href={viewingTask.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-blue-50 rounded-sm border border-blue-100 hover:bg-blue-100 transition-colors"
                  >
                    <Paperclip size={16} className="text-[#002FA7]" />
                    <span className="text-sm text-[#002FA7] flex-1">View Attachment</span>
                    <DownloadSimple size={16} className="text-zinc-400" />
                  </a>
                </div>
              )}

              {/* Status History */}
              {viewingTask.status_history?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Status History</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewingTask.status_history.map((h, idx) => (
                      <div key={h.changed_at || `hist-${idx}`} className="p-2 bg-zinc-50 rounded-sm border border-zinc-100 text-sm">
                        <div className="flex items-center justify-between">
                          <Badge className={`${getStatusBadgeClass(h.status)} text-xs`}>{h.status}</Badge>
                          <span className="text-xs text-zinc-400">{formatDate(h.changed_at)}</span>
                        </div>
                        {h.comment && <p className="mt-1 text-zinc-600 text-xs">{h.comment}</p>}
                        <p className="text-xs text-zinc-400 mt-0.5">by {users[h.changed_by] || h.changed_by}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2 border-t border-zinc-100">
                <Button variant="outline" onClick={() => setViewingTask(null)} className="rounded-sm">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this task?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
